// useRoomTypes — PowerSync-backed hook for the `GET /room-types`
// list consumed by `<NewRoomDialog>` on the BuildingCaptureScreen.
// Replaces the inline `fetchRoomTypes` REST helper.
//
// All authenticated users receive the full room-types list via the
// `reference_global` sync stream — no scope parameter, no stale-data
// guard needed. The empty-state gate uses `useAllSyncStreamsHaveSynced`
// for the same reason as `useBases` / `useSites`: a freshly installed
// device shouldn't briefly render "no room types" before the first
// reference sync delivers them.

import { useAllSyncStreamsHaveSynced, useQuery } from '@powersync/react'

import { db } from './client'

export interface UseRoomTypesRow {
  id: string
  contractId: string
  name: string
  isSerialisable: boolean
  displayOrder: number
}

export interface UseRoomTypesResult {
  roomTypes: UseRoomTypesRow[]
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

interface RoomTypeSqlRow {
  id: string
  contract_id: string
  name: string
  is_serialisable: number
  display_order: number
}

export function useRoomTypes(): UseRoomTypesResult {
  const result = useQuery<RoomTypeSqlRow>(
    `SELECT id, contract_id, name, is_serialisable, display_order
     FROM room_types
     ORDER BY display_order ASC, name ASC`,
    [],
  )

  const referenceGlobalSynced = useAllSyncStreamsHaveSynced(db, [
    { name: 'reference_global', waitForStream: true },
  ])

  const isEmptyButUnsynced =
    result.data.length === 0 && !referenceGlobalSynced
  const isLoading = result.isLoading || isEmptyButUnsynced

  return {
    roomTypes: result.data.map((row) => ({
      id: row.id,
      contractId: row.contract_id,
      name: row.name,
      isSerialisable: row.is_serialisable === 1,
      displayOrder: row.display_order,
    })),
    isLoading,
    isFetching: result.isFetching,
    error: result.error,
  }
}
